import React, { useState, useRef, useMemo } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Bell, Check, CheckCheck, Trash2, Eye, AlertTriangle, Info, X } from "lucide-react"
import { useLocation } from "react-router-dom"

import type { Notification } from "@types"
import useClickOutside from "@hooks/useClickOutside"
import { Button } from "../ui"

interface NotificationPreviewProps {
  notifications: Notification[]
  onMarkAsRead: (id: string) => void
  onMarkAllAsRead: () => void
  onDelete: (id: string) => void
  onViewAll: () => void
}

const NotificationPreview: React.FC<NotificationPreviewProps> = ({
  notifications,
  onMarkAsRead,
  onMarkAllAsRead,
  onDelete,
  onViewAll,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const location = useLocation()

  useClickOutside(dropdownRef, () => setIsOpen(false))

  const unreadCount = notifications.filter((n) => !n.isRead).length
  const recentNotifications = notifications.slice(0, 5)

  const getNotificationIcon = (type: Notification["type"]) => {
    switch (type) {
      case "success":
        return <Check strokeWidth={3} size={16} className="text-green-600 dark:text-green-400" />
      case "error":
        return <X strokeWidth={3} size={16} className="text-red-600 dark:text-red-400" />
      case "warning":
        return <AlertTriangle strokeWidth={3} size={16} className="text-yellow-600 dark:text-yellow-400" />
      case "info":
      default:
        return <Info strokeWidth={3} size={16} className="text-blue-600 dark:text-blue-400" />
    }
  }

  const getNotificationColor = (type: Notification["type"]) => {
    switch (type) {
      case "success":
        return "text-green-600 dark:text-green-400"
      case "error":
        return "text-red-600 dark:text-red-400"
      case "warning":
        return "text-yellow-600 dark:text-yellow-400"
      case "info":
      default:
        return "text-blue-600 dark:text-blue-400"
    }
  }

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
    if (diffInMinutes < 1) return "Now"
    if (diffInMinutes < 60) return `${diffInMinutes}m`
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`
    return `${Math.floor(diffInMinutes / 1440)}d`
  }

  const onMarkAsReadHandler = (ev: React.MouseEvent<HTMLButtonElement>, notification: Notification) => {
    ev.stopPropagation()
    onMarkAsRead(notification.id)
  }

  const showlinkSeeAll = useMemo(() => location.pathname !== "/notifications", [location.pathname])

  const onViewAllHandler = () => {
    setIsOpen(false)
    onViewAll()
  }

  if (!showlinkSeeAll) return null

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg hover:bg-legacy-light/20 dark:hover:bg-legacy-light/10 transition-colors duration-200 relative"
      >
        <Bell className="h-5 w-5 text-legacy-dark dark:text-legacy-cream" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-0 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-legacy-dark rounded-lg shadow-lg border border-legacy-light/20 dark:border-legacy-light/10 z-50"
          >
            <div className="p-4 border-b border-legacy-light/20 dark:border-legacy-light/10">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-legacy-dark dark:text-legacy-cream">Notificaciones</h3>
                <div className="flex items-center space-x-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={onMarkAllAsRead}
                      className="p-1 rounded hover:bg-legacy-light/20 dark:hover:bg-legacy-light/10 transition-colors"
                      title="Mark all as read"
                    >
                      <CheckCheck className="h-4 w-4 text-legacy-dark dark:text-legacy-cream" />
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="max-h-96 overflow-y-auto overflow-x-hidden">
              {recentNotifications.length === 0 ? (
                <div className="p-4 text-center text-legacy-dark/60 dark:text-legacy-cream/60">
                  No notifications found
                </div>
              ) : (
                <div className="divide-y divide-legacy-light/20 dark:divide-legacy-light/10">
                  {recentNotifications.map((notification) => (
                    <motion.div
                      key={notification.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className={`py-4 px-2 hover:bg-legacy-light/10 dark:hover:bg-legacy-light/5 transition-colors ${
                        !notification.isRead ? "bg-blue-50/50 dark:bg-blue-900/20" : ""
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-1">
                          <span className="text-lg">{getNotificationIcon(notification.type)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className={`text-sm font-medium ${getNotificationColor(notification.type)}`}>
                                {notification.title}
                              </p>
                              <p className="text-xs text-legacy-dark/80 dark:text-legacy-cream/80 mt-1 line-clamp-2">
                                {notification.message}
                              </p>
                              <p className="text-xs text-legacy-dark/60 dark:text-legacy-cream/60 mt-1">
                                {formatTimeAgo(notification.createdAt)}
                              </p>
                            </div>
                            <div className="flex flex-col items-center space-y-1">
                              {!notification.isRead && (
                                <Button
                                  onClick={(ev) => onMarkAsReadHandler(ev, notification)}
                                  title="Mark as read"
                                  variant="ghost"
                                  size="xs"
                                >
                                  <Eye size={12} className="text-legacy-dark dark:text-legacy-cream" />
                                </Button>
                              )}
                              <Button
                                onClick={() => onDelete(notification.id)}
                                variant="ghost-danger"
                                title="Delete"
                                size="xs"
                              >
                                <Trash2 size={12} className="text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
            {notifications.length > 5 && (
              <div className="p-3 border-t border-legacy-light/20 dark:border-legacy-light/10">
                <button
                  onClick={onViewAllHandler}
                  className="w-full text-center text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  See {notifications.length - 5} more notifications
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default NotificationPreview
